import { useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { formatCountdown, messageForError } from '@proctoring/api-client';
import { JOIN_WINDOW_LEAD_MINUTES } from '@proctoring/shared';
import { useCandidateProfile } from '../../api/queries.js';
import { useApi } from '../../auth/AuthProvider.js';
import { DownloadPanel } from '../../components/DownloadPanel.js';
import { FullPageMessage } from '../../components/FullPageMessage.js';
import { StateBadge } from '../../components/StateBadge.js';
import { formatDateTime } from '../../components/format.js';
import { useJoinWindow } from './useCountdown.js';

/** The desktop app registers this scheme; opening it hands the interview to the kiosk. */
function deepLinkFor(interviewId: string): string {
  return `proctoring://join/${interviewId}`;
}

export interface CandidateInterviewPageProps {
  /**
   * How the deep link is opened. Injectable because jsdom cannot navigate and would log
   * "Not implemented: navigation" for every test that exercises the hand-off.
   */
  openDeepLink?: (href: string) => void;
}

/**
 * The candidate's pre-interview page.
 *
 * There is deliberately no browser session here. Candidates take the interview in the
 * Electron kiosk (design §2: "Desktop app only") — a browser tab cannot be locked down,
 * cannot enumerate windows, and cannot publish a full-display capture reliably, so
 * offering one would quietly void the proctoring guarantee. This page therefore gates on
 * time and then hands off to the desktop app.
 */
export function CandidateInterviewPage({
  openDeepLink = (href) => globalThis.location.assign(href),
}: CandidateInterviewPageProps = {}): ReactElement {
  const { interviewId = '' } = useParams();
  const api = useApi();
  const { data, isPending, error } = useCandidateProfile();

  const [joinError, setJoinError] = useState<string | null>(null);
  const [handedOff, setHandedOff] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const interview = data?.upcomingInterview ?? null;
  const matches = interview?.id === interviewId;
  const window = useJoinWindow(matches ? interview : null);

  if (isPending) return <FullPageMessage title="Loading your interview…" />;
  if (error) {
    return (
      <FullPageMessage title="Could not load your interview" detail={messageForError(error)} />
    );
  }
  if (!interview || !matches) {
    return (
      <FullPageMessage
        title="Interview not found"
        detail="This interview is not on your account, or it has already finished."
      >
        <Link to="/candidate" className="btn-secondary">
          Back to profile
        </Link>
      </FullPageMessage>
    );
  }

  /**
   * Requests the join token, then opens the deep link.
   *
   * The token is requested even though the desktop app will mint its own: it is the
   * cheapest way to have the *server* confirm the window is genuinely open before telling
   * the candidate to switch apps, so a clock-skewed browser cannot send them into a kiosk
   * that will immediately reject them.
   */
  async function onJoin(): Promise<void> {
    setJoinError(null);
    setRequesting(true);
    try {
      await api.candidateJoin(interviewId);
      setHandedOff(true);
      openDeepLink(deepLinkFor(interviewId));
    } catch (cause) {
      setJoinError(messageForError(cause));
    } finally {
      setRequesting(false);
    }
  }

  const waiting = window?.state === 'waiting';
  const closed = window?.state === 'closed';
  const canJoin = window?.state === 'open';

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-session-text">
            {interview.jobProfile.name} interview
          </h1>
          <p className="mt-1 text-sm text-session-muted">{formatDateTime(interview.scheduledAt)}</p>
        </div>
        <StateBadge state={interview.state} />
      </div>

      <section className="panel mt-8 p-5" aria-labelledby="join-heading">
        <h2 id="join-heading" className="text-sm font-semibold text-session-text">
          Join your interview
        </h2>

        {closed ? (
          <p className="mt-3 text-sm text-session-muted">
            This interview is {window.reason === 'completed' ? 'over' : 'cancelled'}. The room can
            no longer be joined.
          </p>
        ) : (
          <p className="mt-3 text-sm text-session-muted">
            The room opens {JOIN_WINDOW_LEAD_MINUTES} minutes before the scheduled start.
          </p>
        )}

        {waiting ? (
          <p data-testid="join-countdown" className="mt-4 text-sm text-session-muted">
            Opens in{' '}
            <span className="session-tabular-nums font-medium text-session-text">
              {formatCountdown(window.msUntilOpen)}
            </span>
          </p>
        ) : null}

        {joinError ? (
          <p role="alert" className="form-error mt-4">
            {joinError}
          </p>
        ) : null}

        {handedOff ? (
          <p className="mt-4 rounded-md border border-severity-info/40 bg-severity-info/10 px-3 py-2 text-sm text-severity-info">
            Opening the desktop app… If nothing happened, launch the Proctoring app manually and
            sign in there.
          </p>
        ) : null}

        <button
          type="button"
          data-testid="join-button"
          disabled={!canJoin || requesting}
          onClick={() => void onJoin()}
          className="btn-primary mt-5"
        >
          {requesting ? 'Preparing…' : 'Open in desktop app'}
        </button>
      </section>

      {/*
        The install surface is a shared component rather than markup here: the same list has
        to appear on the marketing site, and two copies of "which file does a candidate on
        an M-series Mac need" would drift the moment a packaging target changes.
      */}
      <DownloadPanel
        footer={
          <>
            Already installed?{' '}
            <a href={deepLinkFor(interviewId)} className="text-session-accent underline">
              Open this interview in the app
            </a>
            .
          </>
        }
      />
    </div>
  );
}
